import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, OnInit, ViewChild, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatIcon } from '@angular/material/icon'
import { MatTab, MatTabContent, MatTabGroup, MatTabLabel } from '@angular/material/tabs'
import { ActivatedRoute } from '@angular/router'
import {
  ElderHeaderComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderSinglePaneWrapperComponent,
  ElderTabDirective,
  ElderTabGroupRoutingDirective,
  ElderToastService,
  ElderToolbarColumnDirective,
  Pageable,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject } from 'rxjs'
import { EntityLinkComponent } from '../../../../entitylinks/entity-link/entity-link.component'
import { TransitAssignment } from '../../../transit/prod-order-transit'
import { WorkOrder } from '../../../work-orders/work-order'
import { WorkOrderFilterBuilder } from '../../../work-orders/work-order-filter-builder'
import { WorkOrderFullComponent } from '../../../work-orders/work-order-full/work-order-full.component'
import { WorkOrderService } from '../../../work-orders/work-order.service'
import { ProdOrder } from '../../prod-order'
import { ProdOrderService } from '../../prod-order.service'
import { ProdOrderDetailComponent } from '../prod-order-detail/prod-order-detail.component'
import { ProdOrderDispatchComponent } from '../prod-order-dispatch/prod-order-dispatch.component'

interface KeyValue {
  readonly key: string
  readonly value: string
}

class ProdOrderContext {
  public static order(order: ProdOrder): ProdOrderContext {
    return new ProdOrderContext(false, order.id, order, null)
  }

  public static error(orderId: string, err: string): ProdOrderContext {
    return new ProdOrderContext(false, orderId, null, err)
  }

  public static loading(orderId: string): ProdOrderContext {
    return new ProdOrderContext(true, orderId, null, null)
  }

  constructor(
    public readonly loading: boolean,
    public readonly orderNumber: string,
    public readonly order: ProdOrder,
    public readonly error: string
  ) {}
}

@Component({
  selector: 'app-prod-order',
  templateUrl: './prod-order.component.html',
  styleUrls: ['./prod-order.component.scss'],
  imports: [
    ElderSinglePaneWrapperComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
    ElderPaneContentComponent,
    ElderToolbarColumnDirective,
    ElderHeaderComponent,
    EntityLinkComponent,
    MatTabGroup,
    ElderTabGroupRoutingDirective,
    MatTab,
    ElderTabDirective,
    ProdOrderDetailComponent,
    MatIcon,
    MatTabLabel,
    MatTabContent,
    WorkOrderFullComponent,
    ProdOrderDispatchComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class ProdOrderComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly logger = LoggerFactory.getLogger(this.constructor.name)

  public readonly orderContext$ = new BehaviorSubject<ProdOrderContext>(
    ProdOrderContext.loading('')
  )
  public readonly orderAttributes$ = new BehaviorSubject<KeyValue[]>(null)

  public readonly workOrders$ = new BehaviorSubject<WorkOrder[]>([])

  @ViewChild(MatTabGroup, { static: false })
  public tabGroup: MatTabGroup

  public tabIndex: number

  private route = inject(ActivatedRoute)
  private prodOrderService = inject(ProdOrderService)
  private workOrderService = inject(WorkOrderService)
  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.workOrderService.dataChanged
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.reloadWorkOrdersForProdOrder(this.order.id))
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    const prodOrderId = this.route.snapshot.params['orderId']
    this.reloadProdOrder(prodOrderId)
    this.reloadWorkOrdersForProdOrder(prodOrderId)
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get order(): ProdOrder {
    return this.orderContext$.getValue().order
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onWorkOrderDeleted(): void {
    this.selectFirstTab()
  }

  public onAssignmentChange(_: TransitAssignment) {
    this.reloadWorkOrdersForProdOrder(this.order.id)
  }

  public reloadWorkOrdersForProdOrder(prodOrderId: string): void {
    this.workOrderService
      .findAllPaged(
        new Pageable(0, 100),
        WorkOrderFilterBuilder.start().prodOrders(prodOrderId).allowEmpty(false).build()
      )
      .subscribe({
        next: (page) => this.workOrders$.next(page.content),
        error: (err) => this.toastService.error('Failed to load WorkOrders!', err),
      })
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private reloadProdOrder(prodOrderId: string): void {
    this.orderContext$.next(ProdOrderContext.loading(prodOrderId))

    this.prodOrderService.findById(prodOrderId).subscribe({
      next: (order) => {
        this.logger.info('Successfully loaded order: ', order)
        this.orderContext$.next(ProdOrderContext.order(order))
        this.orderAttributes$.next(this.buildProperties(order))
        // this.reloadOrderItemOverview(orderId);
      },
      error: (err) => {
        this.logger.error('Failed to load order ' + prodOrderId, err)
        this.orderContext$.next(ProdOrderContext.error(prodOrderId, err.error.message))
      },
    })
  }

  private selectFirstTab(): void {
    this.tabIndex = 0
  }

  private buildProperties(order: ProdOrder): KeyValue[] {
    if (order && order.properties) {
      return Object.keys(order.properties).map((key) => {
        return {
          key: key,
          value: order.properties[key],
        }
      })
    } else {
      return []
    }
  }
}
